import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import TrackString from "../../../analytics/TrackString";
import SoundType from "../../../core/sound/util/SoundType";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import ConversationBriefingRecapEvent from "../event/ConversationBriefingRecapEvent";
import ConversationHintEvent from "../event/ConversationHintEvent";
import ConversationStateChangedEvent from "../event/ConversationStateChangedEvent";
import Conversation from "../model/Conversation";

export default class ConversationCoachPanelMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {RevisableLanguagePack} languagePack 
     * @param {SoundManager} soundManager 
     */
    constructor(target, dispatcher, layoutManager, languagePack, soundManager) {
        super(target, dispatcher, layoutManager);

        this._languagePack = languagePack;
        this._soundManager = soundManager;

        this._interactionEnabled = true;
        this._shown = true;
        /** @type {Conversation} */
        this._conversation = null;
        this._hint = "";
    }

    initialize() {
        super.initialize();

        this._target.headerTextField.text = this._languagePack.getText(`conversationScreen.coachPanel.header`);
        this._hintButton = this.createBracketButton(this._target.hintButton, this._handleHintButton.bind(this), this._languagePack.getText(`conversationScreen.coachPanel.hintButtonLabel`), SoundType.BRACKET_BUTTON_CLICK);
        this._briefingButton = this.createBracketButton(this._target.briefingButton, this._handleBriefingButton.bind(this), this._languagePack.getText(`conversationScreen.coachPanel.briefingButtonLabel`), SoundType.BRACKET_BUTTON_CLICK);

        this.registerEventListener(ConversationStateChangedEvent.TYPE, this._handleConversationStateChanged.bind(this));
    }

    async toggle(delay = 0, punch = false) {
        const shown = this._shown;
        this._shown = !shown;

        if (shown) {
            await this.hide();
        } else {
            await this.show(delay, punch);
        }
    }

    async show(delay = 0, punch = false) {
        if (punch) {
            this._target.scale = .5;
        }

        this._shown = true;
        this._interactionEnabled = true;

        // Show buttons to enable hover sounds from playing.
        this._hintButton.setVisible(true);
        this._briefingButton.setVisible(true);

        await createjs.Tween.get(this._target)
            .wait(delay)
            .call(() => this._soundManager.playSound(SoundType.COACH_CHAT_BOX, null, null, 50))
            .to({
                scale: 1,
                alpha: 1,
            }, 150, createjs.Ease.backOut)
            .toPromise();
    }

    async hide() {
        this._interactionEnabled = false;
        this._shown = false;

        await createjs.Tween.get(this._target)
            .to({
                scale: .5,
                alpha: 0,
            }, 150, createjs.Ease.sineIn)
            .toPromise();
        
        // Hide buttons to disable hover sounds from playing.
        this._hintButton.setVisible(false);
        this._briefingButton.setVisible(false);
    }

    hideInstant() {
        this._target.alpha = 0;
        this._target.scale = .5;

        this._shown = false;
    }

    _handleHintButton() {
        if (!this._interactionEnabled) {
            return;
        }

        // Track analytics.
        this.trackEvent(TrackString.EVENT_HINT_REQUEST, TrackString.createHintRequestParams({
            conversationId: this._conversation.id,
            phaseId: this._conversation.currentPhaseId,
            turnIndex: this._conversation.getTotalTurns(),
        }));

        // Notify the conversation to show the hint.
        const conversationHintEvent = new ConversationHintEvent(ConversationHintEvent.PERFORM, this._hint);
        this.dispatch(conversationHintEvent.type, { data: conversationHintEvent });
    }

    _handleBriefingButton() {
        if (!this._interactionEnabled) {
            return;
        }

        this.dispatch(ConversationBriefingRecapEvent.PERFORM);
    }

    _handleConversationStateChanged(evt) {
        /** @type {ConversationStateChangedEvent} */
        const conversationStateChangedEvent = evt.params.data;

        this._conversation = conversationStateChangedEvent.conversation;
        this._hint = this._languagePack.getText(this._conversation.getHintLpKeyForCurrentPhase())?.trim();
        if (!this._hint) {
            this._hintButton.setEnabled(false);
        }
    }
}